// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## editable.js
//
// When an editable object is attached to a note holder, and editing
// is enabled in the renderer, the note becomes editable by touch
// or mouse events. (GCR TODO: Add keyboard responsiveness, get/lose focus,
// etc... for accessibility.)
import type { ClefName } from 'shared/definitions/musicTheory/clefs';
import type { Stave as VexStave } from 'vexflow';
import EventHandler from './eventHandler';
import Clef from './clef';
import type Note from './note';
import type KeySignature from './keySignature';
import type NoteHolder from './noteHolder';
import type Renderer from './renderer';
import type Editor from './editor';
import type CustomKeySignature from './customKeySignature';

const debug = false;
const log = debug
  ? (...args: any[]) => {
      console.log(...args);
    }
  : () => {};

export interface EditableOptions {
  /** User can change rhythms */
  rhythm: boolean;
  /** User can change accidentals */
  accidental: boolean;
  /** User can change pitch */
  pitch: boolean;
  /** User can delete notes (other than fixedNotes) */
  deleteable: false | ('committedRest' | 'placeHolderRest' | 'ghost' | 'removeNoteHolder')[];
  /** User can select notes or the noteHolder */
  selectable: boolean;
  /** Fixed notes are notes that cannot be changed or deleted by the user */
  fixedNotes: Note[];
  /** Locked pitch means adding/deleting can only happen on a single pitch -- use pitch:true, fixedPitch: Note */
  lockedPitch: Note | false;
  /** The maximum number of notes allowed in a noteHolder */
  maxNotes: number;
  /** Whether to initialize the editable as selected */
  selected: boolean;
  /** The highest ledger line allowed -- typically 7 (2 legers above staff) */
  highestLine: number;
  /** The lowest ledger line allowed -- typically -1 (2 legers below staff) */
  lowestLine: number;
  /** If provided, when notes are dragged &/or hovered, apply the accidentals of the key signature */
  keySignature?: KeySignature | CustomKeySignature;
  /** If linked to a second editable which is full, we should display a rest on this editable's noteHolder */
  restIfEmptyAndLinkedFull: boolean;
}

interface BoundingBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

const selectedNotes = Symbol('selectedNotes');

export default class Editable {
  options: EditableOptions;

  /** assigned at render time -- typically a Vex.Flow.StaveNote */
  vexObject: any;

  /** Is the editable selected? */
  selected = false;

  /** Whether the note is temporary, e.g., based on user hover */
  pendingNote: false | Note = false;

  /** Whether the rest is uncommited, e.g., based on user hover */
  pendingRest = false;

  /** When 'rest', the note added is automatically a rest; otherwise a 'note' */
  pendingMode: 'note' | 'rest' = 'note';

  /**
   * Whether a placeHolder rest is showing -- e.g., showing visable space that
   * a note might eventually go in. (Generally a pending rest is used where the
   * full noteHolder is not allowed to be removed from the music.)
   */
  placeHolderRest = false;

  /** Attached noteHolder that becomes editable */
  noteHolder: NoteHolder;

  /** Assigned at render time */
  eventHandler: EventHandler;

  /** Assigned at render time */
  boundingBox: BoundingBox;

  /** uTheory Clef object */
  clef?: Clef;

  /** Assigned at render time, the VexFlow Stave */
  stave?: VexStave;

  /** For use in a grand staff */
  linkedEditable?: Editable;

  renderer?: Renderer;

  editor?: Editor;

  /**
   * If a text input has been rendered for accessibility reasons, this is the element holding it.
   * We keep it on the editable to track if it has previously been added to the dom, or if it
   * needs to be created on this render cycle.
   */
  accessibleInputDiv?: HTMLDivElement;

  private [selectedNotes]: Note[];

  // Public constructor
  constructor(noteHolder: NoteHolder, options: Partial<EditableOptions> = {}) {
    this.options = {
      rhythm: false,
      accidental: false,
      pitch: false,
      deleteable: false,
      selected: false,
      selectable: false,
      lockedPitch: false,
      fixedNotes: [],
      maxNotes: 1,
      highestLine: 7,
      lowestLine: -1,
      restIfEmptyAndLinkedFull: false,
      ...options,
    };

    this.vexObject = null;

    // merge options argument into this.options
    this.selected = this.options.selected;
    this[selectedNotes] = [];

    // Determine what kind of noteHolder it is & initiate
    this.noteHolder = noteHolder;
    this.eventHandler = new EventHandler(this.noteHolder, this);
    this.boundingBox = { x: 0, y: 0, w: 0, h: 0 };
  }

  getLowestPossibleNote(): Note {
    if (!this.clef) {
      throw Error('Must call editable.setClef before calling editable.getLowestPossibleNote');
    }
    return this.clef.getNoteAtLine(this.options.lowestLine);
  }

  getHighestPossibleNote(): Note {
    if (!this.clef) {
      throw Error('Must call editable.setClef before calling editable.getHighestPossibleNote');
    }
    return this.clef.getNoteAtLine(this.options.highestLine);
  }

  getRange(): [Note, Note] {
    return [this.getLowestPossibleNote(), this.getHighestPossibleNote()];
  }

  noteIsInStaffRange(note: Note): boolean {
    return (
      note.isGreaterThanOrEqualsIgnoreAccidental(this.getLowestPossibleNote()) &&
      note.isLessThanOrEqualsIgnoreAccidental(this.getHighestPossibleNote())
    );
  }

  isFull(notesToIgnore = 0): boolean {
    if (!this.options.pitch) return true;
    return this.currentLength() - notesToIgnore >= this.options.maxNotes;
  }

  setBoundingBox(box: BoundingBox) {
    this.boundingBox = box;
  }

  getBoundingBox() {
    return this.boundingBox;
  }

  setClef(clef: ClefName | Clef) {
    if (!(clef instanceof Clef)) {
      clef = new Clef(clef);
    }
    // uTheory clef object -- used to calculate the line/space for a note.
    this.clef = clef;
  }

  setVexStave(stave: VexStave) {
    // Vex.Flow staff object, used to get Y spacing & co-ordinates for lines.
    this.stave = stave;
  }

  setKeySignature(keySig: KeySignature | CustomKeySignature) {
    this.options.keySignature = keySig;
  }

  /**
   * When pendingMode is 'rest', when the user hovers or clicks an empty noteHolder,
   * the noteHolder will pend or commit a rest. This is typically set by the
   * editor, when its setPendingMode is called.
   * @param restMode
   */
  setPendingMode(pendingMode: 'rest' | 'note') {
    if (!['rest', 'note'].includes(pendingMode)) {
      throw new Error(
        `editable.setPendingMode can take "rest" or "note", but not ${pendingMode}as an argument`
      );
    }
    this.pendingMode = pendingMode;
  }

  // This code might be useful at the point that we add more full
  // editor features -- but, it's presently unused, so I've removed it. (GCR)
  // /**
  //  * Turns the attached noteHolder into a rest or a note, preserving
  //  * the pending or committed nature of the noteHolder.
  //  */
  // makeNoteOrRest(noteOrRest: 'note' | 'rest') {
  //   if (!['rest', 'note'].includes(noteOrRest)) {
  //     throw new Error(
  //       `editable.makeNoteOrRest can take "rest" or "note", but not ${noteOrRest}as an argument`
  //     );
  //   }
  //   if (!this.clef) {
  //     throw Error('Must call editable.setClef before calling editable.makeNoteOrRest');
  //   }

  //   const isRest = noteOrRest === 'rest';
  //   const isNote = !isRest;

  //   if (this.noteHolder.length === 0 && isRest) {
  //     // if we were a pending note, now we're a pending rest...
  //     this.pendingRest = !!this.pendingNote;
  //     this.pendingNote = false;
  //     return;
  //   }

  //   if (this.noteHolder.length === 0 && isNote) {
  //     const pendingNote = this.pendingRest ? this.clef.getNoteAtLine(3) : false;
  //     if (pendingNote && this.options.keySignature) {
  //       this.options.keySignature.applyKeySignatureToNote(pendingNote);
  //     }
  //     return;
  //   }

  //   this.pendingRest = false;
  //   this.pendingNote = false;
  //   this.noteHolder.rest = isRest;

  //   if (isNote && !this.noteHolder.length) {
  //     const newNote = this.clef.getNoteAtLine(3);
  //     if (this.options.keySignature) {
  //       this.options.keySignature.applyKeySignatureToNote(newNote);
  //     }
  //     this.noteHolder.push(newNote);
  //     return;
  //   }

  //   if (isRest && this.noteHolder.length) {
  //     this.noteHolder.length = 0;
  //   }

  //   // GCR: Remember prior state to switch back and forth?
  // }

  link(
    editable: Editable,
    options: { maxNotes?: number; restIfEmptyAndLinkedFull?: boolean } = {}
  ) {
    let { maxNotes, restIfEmptyAndLinkedFull } = options;
    // Prefer, first, passed maxNotes. After that, editable.maxNotes,
    // and as a last resort, this.maxNotes.
    maxNotes = typeof maxNotes === 'undefined' ? this.options.maxNotes : maxNotes;
    restIfEmptyAndLinkedFull =
      typeof restIfEmptyAndLinkedFull !== 'undefined'
        ? restIfEmptyAndLinkedFull
        : this.options.restIfEmptyAndLinkedFull;

    log('restIfEmptyAndLinkedFull', restIfEmptyAndLinkedFull);
    this.options.restIfEmptyAndLinkedFull = restIfEmptyAndLinkedFull;
    editable.options.restIfEmptyAndLinkedFull = restIfEmptyAndLinkedFull;

    this.linkedEditable = editable;
    this.options.maxNotes = maxNotes;
    editable.options.maxNotes = maxNotes;
    editable.linkedEditable = this;
  }

  shouldRenderAsRestBecauseLinkedIsFull(): boolean {
    // We should render as a rest if:
    // 1) This is a linked editable and the other editable is full
    if (this.linkedEditable) {
      const linked = !!this.linkedEditable;
      const full = !!this.options.maxNotes && this.currentLength() >= this.options.maxNotes;
      const shouldRenderAsRest = linked && full && this.options.restIfEmptyAndLinkedFull;
      return shouldRenderAsRest;
    }
    return false;
  }

  currentLength() {
    if (this.linkedEditable) {
      return this.linkedEditable.noteHolder.length + this.noteHolder.length;
    }
    return this.noteHolder.length;
  }

  setEditor(editor: Editor) {
    this.editor = editor;
  }

  setRenderer(renderer: Renderer) {
    this.renderer = renderer;
  }

  getRenderer() {
    return this.renderer;
  }

  redraw() {
    if (!this.renderer) {
      throw Error('Must call editable.setRenderer before calling editable.redraw');
    }
    this.renderer.render();
  }

  toString() {
    const str = `Editable() : \n${this.noteHolder}\n Box: ${this.getBoundingBox}`;
    log(str);
  }

  select(bool = true) {
    if (bool === false) {
      this.selected = false;
      this[selectedNotes] = [];
      return;
    }
    if (!this.options.selectable) {
      this.selected = false;
      return;
    }
    if (this.noteHolder.length === 0 && this.noteHolder.rest) {
      this.selected = true;
      return;
    }
    // If we have nothing we can select, don't do it!
    const selectableNotes = this.noteHolder.notes.filter(
      (note) => !this.eventHandler.isFixedNote(note)
    );
    if (selectableNotes.length) {
      this.selected = true;
      this[selectedNotes] = selectableNotes;
    }
  }

  /** */
  get selectedNotes() {
    // filter any notes that may have been removed from the noteHolder
    this[selectedNotes] = this[selectedNotes].filter((note) =>
      this.noteHolder.notes.find((n) => n.equals(note))
    );
    return this[selectedNotes];
  }

  /**
   * @param {Note[]} notes
   */
  set selectedNotes(notes: Note[]) {
    this[selectedNotes] = notes;
  }

  unselect() {
    this.selected = false;
    if (this.hasOwnProperty('eventHandler')) this.eventHandler.unselect();
  }
}
